import { page } from "$app/state";
import type { FacetFilter, FilterObject, Locale, Sort } from "@agapi/mlc-core";
import type { ContentState, Filter, SortOption } from "./collection-store.types";
import type { ProductCollectionResult } from "../../types/collections";
import { getLocaleFromUrl } from '../../utils/locale.shared';

import * as m from '$lib/parachute/messages';
import type { SortBy } from "@agapi/commerce-loop54/src/loop54/types";

export const DEFAULT_LIMIT = 24;

export const clearSelectedFilters = (content: ContentState) => {
  Object.entries(content.filtersSelected).forEach(([key, value]) => {
    if (value.type === 'distinct') {
      content.filtersSelected[key] = { ...value, values: [] };
    } else if (value.type === 'range') {
      content.filtersSelected[key] = { ...value, values: { min: undefined, max: undefined } };
    }
  });
};

export const getSortBy = (sortOption?: SortOption): SortBy | undefined => {
  if (!sortOption) {
    return undefined;
  }
  const sortBy = sortOption.type === 'attribute' ? {
    type: sortOption.type,
    attributeName: sortOption.attributeName,
    order: sortOption.order
  } : {
    type: sortOption.type,
    order: sortOption.order
  };

  // Default sort in Loop54 is "Relevance" so set it to undefined to remove the "Clear filters" button
  return sortBy.type === 'relevance' ? undefined : sortBy;
};

export const isFilteredOrSorted = (content: ContentState, filters: Record<string, Filter>): boolean => {
  const isSorted = Boolean(content.sortBy);
  const isFiltered = Boolean((Object.entries(content.filtersSelected))
    .map(([key, value]) => {
      if (value.type === 'range' && !Array.isArray(value.values)) {
        const values = value.values;
        const filter = filters[key] as Filter & { type: 'range' };
        const maxRange = filter?.max;
        const minRange = filter?.min;

        // Resets the range filter if the min or max is equal to the range min or max
        if (values?.min && values?.max && (values.min !== minRange || values.max !== maxRange)) {
          return Object.values(values);
        }
        return [];
      }
      return value.values;
    })
    .flat().length > 0);

  const isFilteredOrSorted = isFiltered || isSorted;

  console.log('isFilteredOrSorted', isFilteredOrSorted, JSON.stringify(content.filtersSelected), JSON.stringify(content.sortBy));
  return isFilteredOrSorted;
}

export const getDefaultFacets = (locale: Locale): Record<string, FilterObject> => {
  return {
    categories: { type: 'distinct', values: [] },
    brand_name: { type: 'distinct', values: [] },
    [`price_amount_${locale.country}`]: { type: 'range' },
  };
};

export const clearFilterFacetsFromSearchParams = (url: URL) => {
  url.searchParams.delete('price-min');
  url.searchParams.delete('price-max');
  url.searchParams.delete('categories');
  url.searchParams.delete('brands');
  url.searchParams.delete('sort');
  return url;
}

export const setFilterFacetsToSearchParams = (url: URL, content: ContentState) => {
  const filtersAndSelections = content.filtersAvailable.reduce<Record<string, FilterObject>>((a, f) => {
    a[f.label] = content.filtersSelected[f.label]?.values ? content.filtersSelected[f.label] : { type: f.type };
    return a;
  }, {});

  const mapping: Record<string, string> = {
    categories: 'categories',
    brand_name: 'brands',
  }

  for (const [key, value] of Object.entries(filtersAndSelections)) {
    if (value.type === 'range') {
      if (value.values?.min) {
        url.searchParams.set('price-min', value.values?.min.toString());
      } else {
        url.searchParams.delete('price-min');
      }

      if (value.values?.max) {
        url.searchParams.set('price-max', value.values?.max.toString());
      } else {
        url.searchParams.delete('price-max');
      }
    } else if (value.type === 'distinct') {
      if (value.values?.length) {
        url.searchParams.set(mapping[key], value.values?.join(','));
      } else {
        url.searchParams.delete(mapping[key]);
      }
    }
  }

  if (content.sortBy && content.sortBy.type !== 'popularity') { // Popularity is default sort
    url.searchParams.set('sort', (content.sortBy.type === 'attribute' ? `price_${content.sortBy.order}` : content.sortBy.type));
  } else {
    url.searchParams.delete('sort');
  }

  return url;
}

export const getSortOptions = (): SortOption[] => {
  const { country } = getLocaleFromUrl(page.url);
  return [
    { value: 'relevance', label: m.recommended(), type: 'relevance', order: 'desc' },
    { value: 'popularity', label: m.mostPopular(), type: 'popularity', order: 'desc' },
    { value: 'price_asc', label: m.priceAscending(), type: 'attribute', attributeName: `price_amount_${country}`, order: 'asc' },
    { value: 'price_desc', label: m.priceDescending(), type: 'attribute', attributeName: `price_amount_${country}`, order: 'desc' },
  ];
};

export function createInitialContentState(data: ProductCollectionResult): ContentState {
  console.log('initated-content-data', data);
  return {
    products: data.items,
    filtersAvailable: data.facetFilters,
    filtersSelected: data.facetFilters.reduce<Record<string, FilterObject>>((a, value) => {
      if (value.type === 'distinct') {
        a[value.label] = { type: value.type, values: [] };
      } else if (value.type === 'range') {
        a[value.label] = { type: value.type, values: undefined };
      }
      return a;
    }, {}),
    sortBy: getSortOption(data.sortBy),
    count: data.count,
    offset: data.offset ?? 0,
    limit: data.limit ?? DEFAULT_LIMIT
  };
}

export function mapFilters(filtersAvailable: FacetFilter[]): Record<string, Filter> {
  return filtersAvailable.reduce<Record<string, Filter>>((acc, f) => {
    if (f.type === 'distinct') {
      acc[f.label] = {
        type: f.type,
        label: f.label,
        items: f.items.map((i) => ({ label: i.label, value: i.label, count: i.count }))
      };
    } else if (f.type === 'range') {
      acc[f.label] = {
        type: f.type,
        label: f.label,
        min: f.item.min,
        max: f.item.max,
        selectedMin: f.item.selectedMin,
        selectedMax: f.item.selectedMax
      };
    }
    return acc;
  }, {});
}

export const getSortOption = (sortBy: Sort[] = []): SortOption | undefined => {
  const options = getSortOptions();
  return options.find((s) => sortBy?.some((o) => o.order === s.order && o.type === s.type));
}

export const getSelectedFilters = (products: ProductCollectionResult) => {
  return Object.entries(products.filtersSelected)
    .reduce<Record<string, FilterObject>>((a, [ key, value ]) => {
      if (value.type === 'range') {
        a[key] = { type: value.type, values: value.values };
      } else if (value.type === 'distinct') {
        a[key] = { type: value.type, values: value.values };
      }
      return a;
    }, {});
}
